package dairy.mynotes.reusablecomponents

import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.sp
import dairy.mynotes.navigation.Navigator
import dairy.mynotes.repository.AuthRepository
import dairy.mynotes.repository.NotesRepository
import dairy.mynotes.ui.theme.AppColors
import kotlinx.coroutines.launch

/**
 * Reusable TopAppBar used on all authenticated screens.
 * Shows a back button (optional) and a logout icon with confirmation dialog.
 *
 * @param title        Title text shown in the top bar
 * @param navigator    Navigator for back navigation and logout
 * @param showBack     Whether to show the back navigation icon (default false)
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AppTopBar(
    title: String,
    navigator: Navigator? = null,
    showBack: Boolean = false
) {
    var showLogoutDialog by remember { mutableStateOf(false) }
    var showDeleteAccountDialog by remember { mutableStateOf(false) }
    var showMenu by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val authRepository = remember { AuthRepository() }
    val notesRepository = remember { NotesRepository() }

    TopAppBar(
        title = {
            Text(
                text = title,
                fontWeight = FontWeight.Bold,
                fontSize = 18.sp,
                color = AppColors.OnPrimary
            )
        },
        navigationIcon = {
            if (showBack) {
                IconButton(onClick = { navigator?.navigateBack() }) {
                    BackIcon(contentDescription = "Back")
                }
            }
        },
        actions = {
            IconButton(onClick = { showMenu = true }) {
                LogoutIcon(contentDescription = "Menu")
            }
            DropdownMenu(
                expanded = showMenu,
                onDismissRequest = { showMenu = false }
            ) {
                DropdownMenuItem(
                    text = { Text("Logout", color = AppColors.TextPrimary) },
                    onClick = {
                        showMenu = false
                        showLogoutDialog = true
                    }
                )
                DropdownMenuItem(
                    text = { Text("Delete Account", color = AppColors.Error) },
                    onClick = {
                        showMenu = false
                        showDeleteAccountDialog = true
                    }
                )
            }
        },
        colors = TopAppBarDefaults.topAppBarColors(
            containerColor = AppColors.Primary,
            titleContentColor = AppColors.OnPrimary,
            navigationIconContentColor = AppColors.OnPrimary,
            actionIconContentColor = AppColors.OnPrimary
        )
    )

    // Logout confirmation dialog
    if (showLogoutDialog) {
        AlertDialog(
            onDismissRequest = { showLogoutDialog = false },
            containerColor = AppColors.Surface,
            title = {
                Text(
                    text = "Logout?",
                    fontWeight = FontWeight.Bold,
                    color = AppColors.TextPrimary
                )
            },
            text = {
                Text(
                    text = "Are you sure you want to logout?",
                    color = AppColors.TextSecondary
                )
            },
            confirmButton = {
                TextButton(onClick = {
                    showLogoutDialog = false
                    scope.launch {
                        authRepository.logout()
                        navigator?.logout()
                    }
                }) {
                    Text("Logout", color = AppColors.Error, fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                TextButton(onClick = { showLogoutDialog = false }) {
                    Text("Cancel", color = AppColors.Primary)
                }
            }
        )
    }

    // Delete Account confirmation dialog
    if (showDeleteAccountDialog) {
        AlertDialog(
            onDismissRequest = { showDeleteAccountDialog = false },
            containerColor = AppColors.Surface,
            title = {
                Text(
                    text = "Delete Account?",
                    fontWeight = FontWeight.Bold,
                    color = AppColors.Error
                )
            },
            text = {
                Text(
                    text = "This will permanently delete your account and ALL your notes. This action cannot be undone.",
                    color = AppColors.TextSecondary
                )
            },
            confirmButton = {
                TextButton(onClick = {
                    showDeleteAccountDialog = false
                    scope.launch {
                        val userId = authRepository.getCurrentUserId()
                        // Step 1: Delete all notes first
                        if (userId != null) {
                            notesRepository.deleteAllUserNotes(userId)
                        }
                        // Step 2: Try to delete Firebase Auth account
                        val result = authRepository.deleteAccount()
                        if (result.isFailure) {
                            // Firebase requires recent login — sign out and go to login
                            // User's notes are already deleted from Firestore
                            authRepository.logout()
                            navigator?.logout()
                        } else {
                            navigator?.logout()
                        }
                    }
                }) {
                    Text("Delete Account", color = AppColors.Error, fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                TextButton(onClick = { showDeleteAccountDialog = false }) {
                    Text("Cancel", color = AppColors.Primary)
                }
            }
        )
    }
}

